<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <h3>Upload Scanned Document</h3>
        <button class="close-btn" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="modal-body">
        <div class="upload-area" 
             @dragover.prevent 
             @dragenter.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false"
             @drop.prevent="handleFileDrop"
             :class="{ 'dragging': isDragging }">
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileSelect" 
            accept="image/*,.pdf"
            style="display: none">
          <div v-if="!selectedFile" class="upload-prompt">
            <div class="upload-icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </div>
            <p>Drag and drop your file here or</p>
            <button @click="triggerFileInput" class="upload-btn">
              <i class="fas fa-folder-open"></i>
              Browse Files
            </button>
            <p class="file-types">Supported formats: Images, PDF</p>
          </div>
          <div v-else class="selected-file">
            <div class="file-info">
              <i class="fas fa-file-alt file-icon"></i>
              <div class="file-details">
                <p class="file-name">{{ selectedFile.name }}</p>
                <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
              </div>
            </div>
            <button class="remove-btn" @click="removeFile" title="Remove file">
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>
        </div>
        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ error }}
        </div>
      </div>
      <div class="modal-footer">
        <button class="cancel-btn" @click="closeModal">
          <i class="fas fa-times"></i>
          Cancel
        </button>
        <button 
          class="submit-btn" 
          :disabled="!selectedFile || uploading"
          @click="uploadFile">
          <i class="fas fa-upload"></i>
          <span v-if="!uploading">Upload</span>
          <span v-else>Uploading...</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import incomingMailService from '../services/incomingMailService';

export default {
  name: 'ScanUploadModal',
  props: {
    show: {
      type: Boolean,
      required: true
    },
    mailId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'upload-success'],
  setup(props, { emit }) {
    const selectedFile = ref(null);
    const isDragging = ref(false);
    const uploading = ref(false);
    const error = ref('');
    const fileInput = ref(null);

    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
        validateAndSetFile(file);
      }
    };

    const handleFileDrop = (event) => {
      isDragging.value = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        validateAndSetFile(file);
      }
    };

    const validateAndSetFile = (file) => {
      // Check file type
      if (!file.type.match(/^image\/.*|application\/pdf$/)) {
        error.value = 'Please upload an image or PDF file';
        return;
      }
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        error.value = 'File size should not exceed 10MB';
        return;
      }
      error.value = '';
      selectedFile.value = file;
    };

    const removeFile = () => {
      selectedFile.value = null;
      if (fileInput.value) {
        fileInput.value.value = '';
      }
    };

    const uploadFile = async () => {
      if (!selectedFile.value) return;

      uploading.value = true;
      error.value = '';      try {
        const formData = new FormData();
        formData.append('files', selectedFile.value);
        formData.append('mailId', props.mailId);

        // Call your API endpoint to upload the file
        const response = await incomingMailService.uploadScan(formData);
        
        if (response.success) {
          emit('upload-success');
          closeModal();
        } else {
          throw new Error(response.message || 'Upload failed');
        }
      } catch (err) {
        error.value = err.message || 'Failed to upload file';
      } finally {
        uploading.value = false;
      }
    };

    const closeModal = () => {
      removeFile();
      error.value = '';
      emit('close');
    };

    return {
      selectedFile,
      isDragging,
      uploading,
      error,
      fileInput,
      triggerFileInput,
      handleFileSelect,
      handleFileDrop,
      removeFile,
      uploadFile,
      closeModal,
      formatFileSize
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.08);
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
}

.modal-header h3 {
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.modal-body {
  padding: 2rem;
}

.upload-area {
  border: 2.5px dashed #e5e7eb;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  transition: all 0.3s ease;
  background: #f8fafc;
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area.dragging {
  border-color: #22c55e;
  background-color: rgba(34, 197, 94, 0.08);
  transform: scale(1.01);
}

.upload-icon {
  background: rgba(34, 197, 94, 0.1);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.upload-icon i {
  font-size: 2.5rem;
  color: #22c55e;
}

.upload-prompt {
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
}

.upload-prompt p {
  color: #4b5563;
  margin: 1rem 0;
  font-size: 1.1rem;
}

.file-types {
  color: #6b7280;
  font-size: 0.95rem;
}

.upload-btn {
  background: #22c55e;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);
}

.upload-btn:hover {
  background: #16a34a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(34, 197, 94, 0.3);
}

.selected-file {
  width: 100%;
  padding: 1.25rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-icon {
  font-size: 2rem;
  color: #4b5563;
}

.file-details {
  text-align: left;
}

.file-name {
  font-weight: 500;
  color: #1f2937;
  margin: 0;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-size {
  color: #6b7280;
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
}

.remove-btn {
  background: #fee2e2;
  color: #ef4444;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.remove-btn:hover {
  background: #fecaca;
  transform: scale(1.05);
}

.remove-btn i {
  font-size: 1rem;
}

.error-message {
  margin-top: 1rem;
  padding: 0.875rem;
  background: #fee2e2;
  border-radius: 8px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  background: #f8fafc;
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #4b5563;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.submit-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  background: #22c55e;
  color: white;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(34, 197, 94, 0.2);
}

.submit-btn:not(:disabled):hover {
  background: #16a34a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(34, 197, 94, 0.3);
}

.submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.submit-btn i,
.cancel-btn i {
  font-size: 1rem;
}
</style>
